import React, { useRef, useState } from "react";
import { Share2 } from "lucide-react";
import { reminderForJob } from "./shareReminders";
import "./LessonNotes.css";

export const SHARE_CAPTION =
  "Enjoy the full experience at: https://hhcourses.com";

export const SHARE_FOOTER = "Enjoy the full experience at hhcourses.com";

function cleanLabel(value) {
  return String(value || "").trim();
}

export function buildShareLabels({
  chapterTitle,
  lessonTitle,
  courseCardTitle,
  courseAuthor,
} = {}) {
  const headline = [cleanLabel(chapterTitle), cleanLabel(lessonTitle)]
    .filter(Boolean)
    .join(" | ");
  const course = cleanLabel(courseCardTitle);
  const author = cleanLabel(courseAuthor);
  const byline = course && author ? `${course} by ${author}` : course || "";

  return {
    title: headline || "HH Courses",
    text: [byline, SHARE_CAPTION].filter(Boolean).join("\n"),
    clipboardText: [headline, byline, SHARE_CAPTION].filter(Boolean).join("\n"),
    headline: headline || "HH Courses",
    byline,
    footer: SHARE_FOOTER,
  };
}

function base64ToFile(imageBase64, mimeType) {
  const binary = atob(imageBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const type = mimeType || "image/png";
  const extension = type.includes("jpeg") || type.includes("jpg") ? "jpg" : "png";
  return new File([bytes], `hhcourses-share.${extension}`, { type });
}

export function buildCertificateShareLabels({
  recipientName,
  courseCardTitle,
} = {}) {
  const course = cleanLabel(courseCardTitle) || "this course";
  const name = cleanLabel(recipientName);
  const title = "Certificate of Completion";
  const completedLine = name
    ? `${name} has completed ${course} on HH Courses!`
    : `I have completed ${course} on HH Courses!`;
  const note = "I highly encourage you to do the course too at hhcourses.com";
  const text = [completedLine, note].join("\n");

  return {
    title,
    text,
    clipboardText: [title, text].join("\n"),
  };
}

export function buildGrammarShareLabels({ chapterTitle, lessonTitle } = {}) {
  const title = "I am doing the Quranic Grammar course on HH Courses!";
  const currentLine = [cleanLabel(chapterTitle), cleanLabel(lessonTitle)]
    .filter(Boolean)
    .join(" - ");
  const progressLine = currentLine ? `Currently on ${currentLine}` : "";
  const text = [progressLine, SHARE_CAPTION].filter(Boolean).join("\n");

  return {
    title,
    text,
    clipboardText: [title, progressLine, SHARE_CAPTION]
      .filter(Boolean)
      .join("\n"),
  };
}

export async function shareTextOnly(labels) {
  const shareMeta = labels || buildGrammarShareLabels();
  const shareData = {
    title: shareMeta.title,
    text: shareMeta.text,
  };

  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return true;
    } catch (error) {
      if (error?.name === "AbortError") return false;
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(shareMeta.clipboardText);
    } catch (error) {
      // Clipboard is optional.
    }
  }
  return true;
}

async function sharePoster({ imageBase64, mimeType, labels }) {
  const file = base64ToFile(imageBase64, mimeType);
  const shareMeta = labels || buildShareLabels();
  const shareData = {
    files: [file],
    text: shareMeta.text,
    title: shareMeta.title,
  };

  if (navigator.share) {
    try {
      if (!navigator.canShare || navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
        return true;
      }
    } catch (error) {
      if (error?.name === "AbortError") return false;
    }
  }

  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(shareMeta.clipboardText);
    } catch (error) {
      // Clipboard is optional; the file was still downloaded.
    }
  }
  return true;
}

export default function ShareSectionDrawer({
  job,
  expanded,
  isDarkMode,
  onClose,
  onRetry,
  onShared,
}) {
  const dragStartY = useRef(null);
  const [sharing, setSharing] = useState(false);

  if (!job) return null;

  const handleShare = async () => {
    if (sharing || job.status !== "ready" || !job.imageBase64) return;
    setSharing(true);
    try {
      const shared = await sharePoster({
        imageBase64: job.imageBase64,
        mimeType: job.mimeType,
        labels: job.labels || buildShareLabels(job),
      });
      if (shared && onShared) {
        onShared(job);
      }
    } finally {
      setSharing(false);
    }
  };

  const handleTouchStart = (event) => {
    dragStartY.current = event.touches[0].clientY;
  };

  const handleTouchEnd = (event) => {
    if (dragStartY.current == null) return;
    const delta = event.changedTouches[0].clientY - dragStartY.current;
    dragStartY.current = null;
    if (delta > 40) onClose();
  };

  const peekLabel =
    job.status === "generating"
      ? "Making a beautiful poster…"
      : job.status === "error"
        ? "Couldn’t make this poster"
        : null;
  const reminder = reminderForJob(job.requestId);
  const shareLabel = job.certificate ? "Share certificate" : "Share lesson";

  return (
    <>
      <button
        type="button"
        className={`share-section-backdrop${expanded ? " visible" : ""}`}
        aria-label="Close share drawer"
        onClick={onClose}
      />
      <div
        className={`share-section-drawer${expanded ? " expanded" : " collapsed"} ${
          isDarkMode ? "dark" : "light"
        } status-${job.status}`}
        onClick={(event) => event.stopPropagation()}
      >
      <button
        type="button"
        className="share-section-handle"
        aria-label="Close share drawer"
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <span className="share-section-handle-bar" />
        {peekLabel ? (
          <span className="share-section-peek">
            {job.status === "generating" && (
              <span className="share-section-pulse" />
            )}
            {peekLabel}
          </span>
        ) : null}
      </button>

      <div className="share-section-body">
        <div className="share-section-visual">
          {job.status === "generating" && (
            <div className="share-section-loading">
              <div className="share-section-poster-frame">
                <div className="share-section-poster-shimmer" />
              </div>
            </div>
          )}

          {job.status === "ready" && (
            <img
              className="share-section-preview"
              alt="Share poster"
              src={`data:${job.mimeType || "image/png"};base64,${job.imageBase64}`}
            />
          )}

          {job.status === "error" && (
            <div className="share-section-error">
              <p>
                {job.error || "Something went wrong while making your poster."}
              </p>
              <div className="share-section-actions">
                <button
                  type="button"
                  className="note-composer-save"
                  onClick={onRetry}
                >
                  Try again
                </button>
              </div>
            </div>
          )}
        </div>

        {job.status !== "error" && reminder ? (
          <div className="share-section-reminder">
            {reminder.arabic ? (
              <p className="share-section-reminder-arabic">{reminder.arabic}</p>
            ) : null}
            <p className="share-section-reminder-english">{reminder.english}</p>
            <p className="share-section-reminder-source">{reminder.source}</p>
            {job.status === "ready" ? (
              <button
                type="button"
                className="share-section-share-btn"
                aria-label={shareLabel}
                onClick={handleShare}
                disabled={sharing}
              >
                <Share2 size={18} strokeWidth={2.5} />
                {shareLabel}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
    </>
  );
}
